// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling.WEB.8 — the one module that carries audio. It speaks the
// Kaption Call Bridge client API (whatsapp-calling.BRIDGE.6) with the
// browser's own RTCPeerConnection: push the agent's mic, pull this agent's
// downlink. No vendor SDK. The same module runs inside rumo-desktop (it is the
// dashboard), where the desktop bridge only adds device + noise preferences.
//
//   1. POST   {bridge_url}/v1/media/session     {sdp_offer} → {sdp_answer, session_id, customer_track}
//      (our offer publishes the mic on one sendonly audio m-line)
//   2. wait for the RTCPeerConnection to be "connected" — the SFU refuses to
//      pull anything into a session before that; a session that does not
//      connect within 4 s is dropped and steps 1-2 run again with a fresh
//      one, up to 2 retries
//   3. POST   {bridge_url}/v1/media/subscribe   → {customer_track, sdp_offer}
//   4. PUT    {bridge_url}/v1/media/renegotiate {sdp}   (our ANSWER to that sdp_offer)
//   5. DELETE {bridge_url}/v1/media/session     (leave)
//
// The remote track that arrives in step 3 is played for the whole call: it
// carries whatever this agent should hear now (customer, consult partner,
// silence). Hold / pass / consult are routing changes inside the relay and
// never renegotiate this device. There is no client-initiated re-offer in
// phase 1 (whatsapp-calling.BRIDGE.6).
// All requests carry `Authorization: Bearer <participant_token>` (BRIDGE.6).
// The bridge API is non-trickle, so offers / answers are sent once ICE
// gathering is complete (or after ICE_GATHER_TIMEOUT_MS with what we have).
import { buildMicStream } from './kaptionNoiseSuppression';

export const CLIENT_STATE = Object.freeze({
  IDLE: 'idle',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  FAILED: 'failed',
  CLOSED: 'closed',
});

// whatsapp-calling.WEB.9 — why a join failed, as the dock and API.12 name it.
export const MEDIA_FAILURE = Object.freeze({
  MIC_BLOCKED: 'mic_blocked',
  AUDIO_FAILED: 'audio_failed',
});

const ICE_GATHER_TIMEOUT_MS = 2500;
// BRIDGE.6 step 2 — how long one published session may take to connect, and
// how many fresh sessions a join tries before it fails (1 + 2 retries). A
// healthy session connects in 1–3 s; a stalled one (ICE stuck in "checking")
// does not recover in place but a new session does.
export const CONNECT_TIMEOUT_MS = 4000;
export const JOIN_ATTEMPTS = 3;
// WEB.9 — a mic request that never settles (a desktop shell that blocks the
// prompt) is a blocked mic, not an endless "connecting".
export const MIC_TIMEOUT_MS = 8000;

const withReason = (error, reason) => {
  const failure = error instanceof Error ? error : new Error(String(error));
  if (!failure.reason) failure.reason = reason;
  return failure;
};

const withTimeout = (promise, timeoutMs, message) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), timeoutMs);
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      error => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

const trimSlash = url => String(url || '').replace(/\/+$/, '');

// ice_servers from API.2 may be snake_case or already RTCIceServer-shaped.
export const toRtcIceServers = (iceServers = []) =>
  (iceServers || []).map(server => ({
    urls: server.urls || server.url,
    ...(server.username ? { username: server.username } : {}),
    ...(server.credential ? { credential: server.credential } : {}),
  }));

const waitForIceGathering = (pc, timeoutMs) =>
  new Promise(resolve => {
    if (pc.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, timeoutMs);
    const onChange = () => {
      if (pc.iceGatheringState === 'complete') {
        clearTimeout(timer);
        pc.removeEventListener?.('icegatheringstatechange', onChange);
        resolve();
      }
    };
    pc.addEventListener?.('icegatheringstatechange', onChange);
  });

// Resolves once the peer connection is "connected"; rejects on failed /
// closed / timeout, or when `cancel` is called (stop() during the join).
const waitForConnected = (pc, timeoutMs) => {
  let cancel = () => {};
  const promise = new Promise((resolve, reject) => {
    if (pc.connectionState === 'connected') {
      resolve();
      return;
    }
    let timer = null;
    const onChange = () => {
      if (pc.connectionState === 'connected') {
        cancel(null);
      } else if (['failed', 'closed'].includes(pc.connectionState)) {
        cancel(new Error(`[WHATSAPP-CALL] media ${pc.connectionState}`));
      }
    };
    cancel = error => {
      clearTimeout(timer);
      pc.removeEventListener?.('connectionstatechange', onChange);
      if (error) reject(error);
      else resolve();
    };
    timer = setTimeout(
      () => cancel(new Error('[WHATSAPP-CALL] media connect timeout')),
      timeoutMs
    );
    pc.addEventListener?.('connectionstatechange', onChange);
  });
  return { promise, cancel: error => cancel(error) };
};

export default class KaptionCallClient {
  constructor({
    bridgeUrl,
    participantToken,
    iceServers = [],
    micDeviceId = null,
    speakerDeviceId = null,
    noiseSuppression = 'standard',
    noiseSuppressorProvider = null,
    onStateChange = () => {},
    onCustomerTrack = () => {},
    connectTimeoutMs = CONNECT_TIMEOUT_MS,
    micTimeoutMs = MIC_TIMEOUT_MS,
    // Injectable for tests and the mock layer.
    RTCPeerConnectionImpl = window.RTCPeerConnection,
    fetchImpl = (...args) => window.fetch(...args),
    mediaDevices = window.navigator?.mediaDevices,
    createAudioElement = () => new Audio(),
  } = {}) {
    this.bridgeUrl = trimSlash(bridgeUrl);
    this.participantToken = participantToken;
    this.iceServers = toRtcIceServers(iceServers);
    this.micDeviceId = micDeviceId;
    this.speakerDeviceId = speakerDeviceId;
    this.noiseSuppression = noiseSuppression;
    this.noiseSuppressorProvider = noiseSuppressorProvider;
    this.onStateChange = onStateChange;
    this.onCustomerTrack = onCustomerTrack;
    this.connectTimeoutMs = connectTimeoutMs;
    this.micTimeoutMs = micTimeoutMs;
    this.RTCPeerConnectionImpl = RTCPeerConnectionImpl;
    this.fetchImpl = fetchImpl;
    this.mediaDevices = mediaDevices;
    this.createAudioElement = createAudioElement;

    this.state = CLIENT_STATE.IDLE;
    this.pc = null;
    this.sessionId = null;
    this.customerTrack = null;
    this.mic = null; // {stream, track, dispose}
    this.sender = null;
    this.audioEl = null;
    this.joining = false;
    this.pendingConnect = null;
    this.muted = false;
    this.onHold = false;
  }

  setState(state) {
    if (this.state === state) return;
    this.state = state;
    this.onStateChange(state);
  }

  async request(method, path, body) {
    const response = await this.fetchImpl(`${this.bridgeUrl}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.participantToken}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      const error = new Error(
        `[WHATSAPP-CALL] bridge ${method} ${path} ${response.status}`
      );
      error.status = response.status;
      throw error;
    }
    if (response.status === 204) return {};
    return response.json().catch(() => ({}));
  }

  async acquireMic(deviceId) {
    return buildMicStream({
      mediaDevices: this.mediaDevices,
      deviceId,
      level: this.noiseSuppression,
      provider: this.noiseSuppressorProvider,
    });
  }

  attachRemote(stream) {
    if (!this.audioEl) {
      this.audioEl = this.createAudioElement();
      this.audioEl.autoplay = true;
    }
    this.audioEl.srcObject = stream;
    this.audioEl.muted = this.onHold;
    if (this.speakerDeviceId && this.audioEl.setSinkId) {
      this.audioEl.setSinkId(this.speakerDeviceId).catch(() => {});
    }
    this.audioEl.play?.()?.catch?.(() => {});
  }

  // WEB.9 — denied, missing or never settling: all "mic_blocked".
  async acquireMicOrFail() {
    const request = this.acquireMic(this.micDeviceId);
    try {
      return await withTimeout(
        request,
        this.micTimeoutMs,
        '[WHATSAPP-CALL] microphone request timed out'
      );
    } catch (error) {
      // A mic that arrives after we gave up must not stay open.
      request.then(late => late?.dispose?.()).catch(() => {});
      throw withReason(error, MEDIA_FAILURE.MIC_BLOCKED);
    }
  }

  // BRIDGE.6 — join: publish the mic, wait for connected, then subscribe to
  // this participant's downlink. Rejects with error.reason mic_blocked |
  // audio_failed (WEB.9); the same client can start() again (Retry).
  async start() {
    this.setState(CLIENT_STATE.CONNECTING);
    this.joining = true;
    try {
      this.mic = await this.acquireMicOrFail();
      // Steps 1 + 2, re-joined with a fresh session when it never connects
      // (BRIDGE.6: a device whose connection fails leaves and re-joins).
      let result;
      for (let attempt = 1; !result; attempt += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          result = await this.publish();
        } catch (error) {
          if (!error.retryable || attempt >= JOIN_ATTEMPTS || !this.pc)
            throw error;
          this.closePeer();
        }
      }
      const { pc } = this;

      // Steps 3 + 4: subscribe, answer the SFU's offer.
      const subscription = await this.request('POST', '/v1/media/subscribe');
      if (subscription.customer_track)
        this.customerTrack = subscription.customer_track;
      if (subscription.sdp_offer)
        await this.answerSfuOffer(subscription.sdp_offer);
      this.onCustomerTrack(this.customerTrack);

      this.joining = false;
      this.handleConnectionState(pc.connectionState);
      return { ...result, customer_track: this.customerTrack };
    } catch (error) {
      this.joining = false;
      this.pendingConnect = null;
      if (this.state !== CLIENT_STATE.CLOSED) {
        this.setState(CLIENT_STATE.FAILED);
        this.releaseLocal();
      }
      throw withReason(error, MEDIA_FAILURE.AUDIO_FAILED);
    }
  }

  // BRIDGE.6 steps 1 + 2 — a new peer connection publishes the mic on its own
  // sendonly m-line (what we hear arrives on the m-line the SFU adds in step
  // 3), the bridge answers, and we wait for "connected": the SFU will not pull
  // into this session before that. A session that never connects throws a
  // retryable error; a refused request does not.
  async publish() {
    const pc = new this.RTCPeerConnectionImpl({ iceServers: this.iceServers });
    this.pc = pc;
    pc.addEventListener('track', event => {
      const [stream] = event.streams || [];
      this.attachRemote(stream || new MediaStream([event.track]));
    });
    pc.addEventListener('connectionstatechange', () => {
      if (this.pc === pc) this.handleConnectionState(pc.connectionState);
    });
    if (pc.addTransceiver) {
      const transceiver = pc.addTransceiver(this.mic.track, {
        direction: 'sendonly',
        streams: [this.mic.stream],
      });
      this.sender = transceiver.sender;
    } else {
      this.sender = pc.addTrack(this.mic.track, this.mic.stream);
    }

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await waitForIceGathering(pc, ICE_GATHER_TIMEOUT_MS);

    const result = await this.request('POST', '/v1/media/session', {
      sdp_offer: pc.localDescription?.sdp || offer.sdp,
    });
    this.sessionId = result.session_id;
    this.customerTrack = result.customer_track;
    await pc.setRemoteDescription({ type: 'answer', sdp: result.sdp_answer });

    this.pendingConnect = waitForConnected(pc, this.connectTimeoutMs);
    try {
      await this.pendingConnect.promise;
    } catch (error) {
      if (this.pc === pc) error.retryable = true;
      throw error;
    } finally {
      this.pendingConnect = null;
    }
    if (this.pc !== pc) throw new Error('[WHATSAPP-CALL] left while joining');
    return result;
  }

  // Drop a peer connection that never connected; the mic stays for the retry.
  // The bridge replaces the participant's old session on the next POST.
  closePeer() {
    try {
      this.pc?.close();
    } catch (e) {
      // already closed
    }
    this.pc = null;
    this.sender = null;
  }

  // Before the subscribe completes the client stays CONNECTING (a connected
  // publish-only session is not a call yet); afterwards a drop shows
  // RECONNECTING and a recovery CONNECTED again.
  handleConnectionState(connectionState) {
    if (this.state === CLIENT_STATE.CLOSED || this.joining) return;
    if (connectionState === 'connected') this.setState(CLIENT_STATE.CONNECTED);
    else if (connectionState === 'disconnected')
      this.setState(CLIENT_STATE.RECONNECTING);
    else if (connectionState === 'failed') this.setState(CLIENT_STATE.FAILED);
  }

  // BRIDGE.6 step 4 — PUT /v1/media/renegotiate {sdp}: answer the SFU's
  // offer that subscribes this device to its downlink.
  async answerSfuOffer(sdpOffer) {
    if (!this.pc) return;
    await this.pc.setRemoteDescription({ type: 'offer', sdp: sdpOffer });
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    await waitForIceGathering(this.pc, ICE_GATHER_TIMEOUT_MS);
    await this.request('PUT', '/v1/media/renegotiate', {
      sdp: this.pc.localDescription?.sdp || answer.sdp,
    });
  }

  setMuted(muted) {
    this.muted = !!muted;
    if (this.mic?.track) this.mic.track.enabled = !this.muted && !this.onHold;
  }

  // Client-side hold: the customer hears silence and the agent hears nothing.
  // A bridge-side hold (music, BRIDGE.9) would replace this without changing
  // the dock.
  setHold(onHold) {
    this.onHold = !!onHold;
    if (this.mic?.track) this.mic.track.enabled = !this.muted && !this.onHold;
    if (this.audioEl) this.audioEl.muted = this.onHold;
  }

  // DESKTOP.4 — switch the mic without renegotiating (replaceTrack).
  async switchMic(deviceId) {
    this.micDeviceId = deviceId;
    if (!this.sender) return;
    const next = await this.acquireMic(deviceId);
    await this.sender.replaceTrack(next.track);
    this.mic?.dispose?.();
    this.mic = next;
    this.setMuted(this.muted);
  }

  async setNoiseSuppression(level) {
    this.noiseSuppression = level;
    if (this.sender) await this.switchMic(this.micDeviceId);
  }

  async setSpeaker(deviceId) {
    this.speakerDeviceId = deviceId;
    if (this.audioEl?.setSinkId) await this.audioEl.setSinkId(deviceId);
  }

  releaseLocal() {
    this.pendingConnect?.cancel(
      new Error('[WHATSAPP-CALL] left while joining')
    );
    this.pendingConnect = null;
    this.mic?.dispose?.();
    this.mic = null;
    if (this.audioEl) {
      this.audioEl.srcObject = null;
      this.audioEl = null;
    }
    if (this.pc) {
      try {
        this.pc.close();
      } catch (e) {
        // already closed
      }
      this.pc = null;
    }
    this.sender = null;
  }

  // BRIDGE.6 — DELETE /v1/media/session. Never throws: hanging up locally
  // must always succeed even if the bridge is unreachable.
  async stop() {
    const hadSession = !!this.sessionId;
    this.releaseLocal();
    this.setState(CLIENT_STATE.CLOSED);
    if (!hadSession) return;
    this.sessionId = null;
    try {
      await this.request('DELETE', '/v1/media/session');
    } catch (e) {
      // the bridge times the participant out (BRIDGE.7 media_timeout)
    }
  }
}
